import { type FieldValues } from "react-hook-form";
import type { ValidFormData } from "src/components/Form/hooks/useResourceForm/useResourceForm.tsx";
import { useCallback } from "react";
import type { FormAction } from "src/components/Form/Form.types.ts";
import { type ValidFormField } from "src/components/Form/Form.types.ts";

import { mapFieldsToFormValues } from "src/components/Form/utils/mapFieldsToFormValues.tsx";
import { getAdditionalFormValues } from "src/components/Form/utils/getAdditionalFormValues.ts";
import type { GlobalSettings } from "src/hooks/use-global-settings.ts";

export const useResourceFormInitialFormValues = <
  FormValues extends FieldValues = FieldValues,
  FormData extends ValidFormData = ValidFormData,
>({
  action,
  transformDataToValues,
  transformResourceToValues,
  globalSettings,
  resolveFields,
  resource,
  params,
  searchParams,
}: {
  action: FormAction;
  resource: string;
  params: Record<string, unknown>;
  searchParams: URLSearchParams;
  globalSettings: GlobalSettings;
  resolveFields: (
    data: FormData | undefined,
  ) => Record<keyof FormValues, ValidFormField<FormValues>>;
  transformResourceToValues?: (
    resource: any,
    globalSettings: GlobalSettings,
  ) => {
    [Key in keyof FormValues]?: FormValues[Key] | null;
  };
  transformDataToValues?: (
    data: FormData,
    type: FormAction,
    globalSettings: GlobalSettings,
  ) => {
    [Key in keyof FormValues]?: FormValues[Key] | null;
  };
}) => {
  return useCallback(
    ({ data }: { data: FormData | undefined }): FormValues => {
      const processedFields = resolveFields(data);

      const getValues = (data: FormData | undefined) => {
        if (!data) {
          return undefined;
        }
        if (transformDataToValues) {
          return transformDataToValues(data, action, globalSettings);
        }

        if (action === "create") {
          return data?.data as unknown as FormValues;
        }

        if (transformResourceToValues) {
          const resource = data.data;
          return transformResourceToValues(resource, globalSettings);
        }

        return data.data;
      };

      const values = getValues(data);

      const initialValues = mapFieldsToFormValues<FormValues>(
        processedFields,
        action,
        params,
        searchParams,
        values,
      ) as unknown as FormValues;

      const additionalValues = getAdditionalFormValues({
        processedFields,
        mainResource: data?.data,
        resource,
        action,
      });

      return {
        ...initialValues,
        ...additionalValues,
      };
    },
    [
      transformDataToValues,
      resource,
      action,
      params,
      resolveFields,
      transformResourceToValues,
      globalSettings,
      searchParams,
    ],
  );
};
